import { useParams, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeftIcon } from "@phosphor-icons/react";

import { useCompaniesSlice } from "pages/Companies/slice/index";
import { selectCompanies } from "pages/Companies/slice/selector";

import { CommonDashboardWrapper } from "styles/GlobalStyles";
import { CompaniesWrapperStyles } from "pages/Companies/styled";
import { CompanyInfoStyles } from "pages/RosterStatusPage/styled";
import { CompanyNameCell, renderValue } from "utils/renderCompanyCell";

import Icon from "components/Icon/Icon";
import Input from "components/Input/index";
import EmptyData from "components/EmptyData/index";
import LoadingSpinner from "components/PageLoader/Spinner";

const ListDomains = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useCompaniesSlice();

  const { companyId } = useParams();
  const location = useLocation();

  // store refs dynamically for each input
  const inputRefs = useRef([]);

  const [isAdding, setIsAdding] = useState(false);
  const { companies, isLoading } = useSelector(selectCompanies);
  const passedCompany = location.state?.company;

  // Get active company
  const activeCompany =
    passedCompany || companies.find((c) => c.id.toString() === companyId);

  // Fetch companies only if not already loaded
  useEffect(() => {
    if (!companies?.length && companyId) {
      dispatch(actions.fetchCompanies({ companyId }));
    }
  }, [dispatch, companyId, companies?.length]);

  // Local state for form data (initialized from activeCompany)
  const [formData, setFormData] = useState({ domains: [] });

  // Whenever activeCompany changes, sync formData
  useEffect(() => {
    if (activeCompany?.domains?.length) {
      setFormData({ domains: activeCompany.domains });
    }
  }, [activeCompany]);

  // Auto-focus newly added input
  useEffect(() => {
    if (isAdding && inputRefs.current.length) {
      const lastRef = inputRefs.current[inputRefs.current.length - 1];
      if (lastRef) lastRef.focus();
      setIsAdding(false);
    }
  }, [formData.domains.length, isAdding]);

  const handleGoBack = () => {
    // Use the query from URL if state is empty
    const query = location.state?.prevSearch || window.location.search;
    navigate(`/companies${query}`);
  };

  return (
    <CommonDashboardWrapper>
      <CompaniesWrapperStyles>
        <div className="go-back-wrapper">
          <div className="go-back-icon" onClick={handleGoBack}>
            <Icon component={ArrowLeftIcon} format="outline" weight="regular" />
          </div>

          {activeCompany && (
            <div className="update-text-wrapper">
              <CompanyNameCell
                logo={activeCompany?.logo}
                name={renderValue(activeCompany?.company_name)}
                suffix="&lsquo;s"
              />
              <h4>Domains</h4>
            </div>
          )}
        </div>
      </CompaniesWrapperStyles>

      <CompanyInfoStyles
        className={`domains-view ${isLoading ? "loading-domains-view" : ""}`}
      >
        {isLoading ? (
          <LoadingSpinner
            color="#797979"
            text="Loading Data..."
            fontSize={16}
          />
        ) : formData.domains?.length === 0 ? (
          <EmptyData
            message={`No Domains for ${activeCompany?.company_name}`}
          />
        ) : (
          <>
            {formData?.domains.length > 0 && (
              <div className="company-form-wrapper domain-list-wrapper">
                {formData.domains.map((d, idx) => (
                  <div key={idx} className="domains-wrapper">
                    <Input
                      id={`domain-${idx}`}
                      label={idx === 0 ? "Domains" : ""}
                      value={d.domain}
                      onChange={(e) => {
                        const newDomains = [...formData.domains];
                        newDomains[idx].domain = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          domains: newDomains
                        }));
                      }}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      readOnly="readOnly"
                      inputWrapperClass="domains-field"
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CompanyInfoStyles>
    </CommonDashboardWrapper>
  );
};

export default ListDomains;
